// Replaces cornerSmoothing.ts's narrow "round off the 4 corners" treatment
// with a general one: wherever the cutout's alpha mask runs flush against
// the image's rectangular bounding box (not just at the corners - any full
// straight run along an edge, e.g. hair or a shoulder cut off by the camera
// frame), that's an artifact of the photo's rectangular border, not the
// subject's real silhouette. A hard straight line there breaks the "organic
// sticker" look even though the ML cutout + guided filter already made the
// rest of the silhouette look natural.
//
// Fix: nibble a smooth, wavy band in from each of the 4 edges using a
// summed-sine noise profile (so the bite depth varies continuously along
// each side instead of being a uniform inset), fading alpha out via a
// smoothstep rather than a hard cut. Pixels already transparent near a
// border are unaffected (fading an already-zero alpha is a no-op), so this
// is safe to apply unconditionally on every upload rather than first
// detecting which edges are actually touched.

export interface OrganicEdgeOptions {
    /** max bite depth as a fraction of the shorter image dimension */
    maxDepthFraction?: number; // default 0.05 (5%)
    maxDepthFloorPx?: number; // default 6
}

const DEFAULTS: Required<OrganicEdgeOptions> = {
    maxDepthFraction: 0.05,
    maxDepthFloorPx: 6,
};

async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };

        img.src = url;
    });
}

async function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Failed to create blob from canvas'));
                return;
            }
            resolve(blob);
        }, 'image/png');
    });
}

// Smoothly-varying [0, maxDepth] profile along one edge, built from a few
// summed sine waves at different frequencies/phases so the bite looks
// organic rather than a uniform or perfectly periodic inset. Amplitudes sum
// to 1 so the result stays within [0, maxDepth].
function biteDepthProfile(length: number, maxDepth: number): Float32Array {
    const waves = [
        { freq: 2 + Math.random() * 2, amp: 0.45, phase: Math.random() * Math.PI * 2 },
        { freq: 5 + Math.random() * 3, amp: 0.3, phase: Math.random() * Math.PI * 2 },
        { freq: 11 + Math.random() * 5, amp: 0.25, phase: Math.random() * Math.PI * 2 },
    ];
    const out = new Float32Array(length);
    for (let i = 0; i < length; i++) {
        const t = (i / length) * Math.PI * 2;
        let v = 0;
        for (const { freq, amp, phase } of waves) {
            v += amp * (0.5 + 0.5 * Math.sin(t * freq + phase));
        }
        out[i] = v * maxDepth;
    }
    return out;
}

function smoothstep(t: number): number {
    const c = Math.max(0, Math.min(1, t));
    return c * c * (3 - 2 * c);
}

export async function applyOrganicEdge(
    file: File,
    opts: OrganicEdgeOptions = {}
): Promise<File> {
    const { maxDepthFraction, maxDepthFloorPx } = { ...DEFAULTS, ...opts };

    const img = await loadImageFromFile(file);

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { data, width, height } = imageData;

    const shortDim = Math.min(width, height);
    const maxDepth = Math.max(maxDepthFloorPx, Math.round(shortDim * maxDepthFraction));

    const depthTop = biteDepthProfile(width, maxDepth);
    const depthBottom = biteDepthProfile(width, maxDepth);
    const depthLeft = biteDepthProfile(height, maxDepth);
    const depthRight = biteDepthProfile(height, maxDepth);

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4 + 3;
            const alpha = data[idx];
            if (alpha === 0) continue;

            let factor = 1;

            const dTop = depthTop[x];
            if (dTop > 0 && y < dTop) factor *= smoothstep(y / dTop);

            const dBottom = depthBottom[x];
            const distBottom = height - 1 - y;
            if (dBottom > 0 && distBottom < dBottom) factor *= smoothstep(distBottom / dBottom);

            const dLeft = depthLeft[y];
            if (dLeft > 0 && x < dLeft) factor *= smoothstep(x / dLeft);

            const dRight = depthRight[y];
            const distRight = width - 1 - x;
            if (dRight > 0 && distRight < dRight) factor *= smoothstep(distRight / dRight);

            if (factor < 1) {
                data[idx] = Math.round(alpha * factor);
            }
        }
    }

    ctx.putImageData(imageData, 0, 0);

    const blob = await canvasToBlob(canvas);
    return new File([blob], file.name, { type: 'image/png' });
}
